import React, { useEffect, useCallback, useState, useDeferredValue } from 'react';
import { FluentSearchFilterBuilder } from './SearchBuilder';
import { useComparisonTimePeriodShortDisplayName, useAppSelector, useSnackbar } from 'src/utils/Hooks';
import { BeaconSubtitleContainer } from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconSubtitleContainer';
import { formatDisplayName } from 'src/utils/stringFormatting';
import { SearchTextField } from 'src/components/BeaconV2Components/TableSearch';
import MetricListContainer from 'src/components/BeaconV2Components/MetricList/MetricListContainer';
import isEqual from 'lodash/isEqual';
import { useMainEntityType } from 'src/utils/Hooks/reduxSelectors';

import AggregationBuilder from 'src/components/BeaconV2Components/utils/AggregationBuilder';
import { GetAdMetricIndexName } from 'src/components/BeaconV2Components/ExperimentalLayout/Advertising/AdMetric/AdMetricsUtils';
import { CPCField, AdClickField, AdSpendField, AdSalesField, ROASField } from '../../AdMetric/AdMetricGrid/constants';
import AdvancedSearchRequestBuilder from 'src/components/BeaconV2Components/utils/AdvancedSearchRequestBuilder';
import {
  CampaignColumnDefinitionNewTab,
  CampaignTypeField,
  CampaignAutomationField,
  RulesCampaignCheckBox,
  TargetColumnDefinition,
  TargetTypeColumnDefinition
} from 'src/components/BeaconV2Components/MetricList/headColumnDefinitions';
import RuleStore from './stores';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import colors from 'src/utils/colors';
import { sortData, campaignSearchTermFilters, targetSearchTermFilters } from './utils';

export const MetricGrid = React.memo(() => {
  const entityType = useMainEntityType();
  const { startDayId, endDayId } = useAppSelector((state) => state.mainTimePeriod);
  const { showSnackbar } = useSnackbar();
  const mainTimePeriod = useAppSelector((state) => state.mainTimePeriod);
  const comparisonDisplayName = useComparisonTimePeriodShortDisplayName();
  const [initialSize, setInitialSize] = useState(null);
  const [originalSelectedData, setOriginalSelectedData] = useState(null);

  const {
    editRuleId,
    targets,
    setTargets,
    campaigns,
    setCampaigns,
    ruleTarget,
    selectedCampaigns,
    selectedTargets,
    searchTerm,
    setSearchTerm
  } = RuleStore((s) => ({
    editRuleId: s.editRuleId,
    targets: s.targets,
    setTargets: s.setTargets,
    campaigns: s.campaigns,
    setCampaigns: s.setCampaigns,
    ruleTarget: s.rules.ruleTarget,
    selectedCampaigns: s.selectedCampaigns,
    selectedTargets: s.selectedTargets,
    searchTerm: s.searchTerm,
    setSearchTerm: s.setSearchTerm
  }));
  const debouncedSearchValue = useDeferredValue(searchTerm);
  const selectedTargetText = ruleTarget === 'adCampaign' ? 'Campaigns' : 'Targets';
  const selectedData = ruleTarget === 'adCampaign' ? selectedCampaigns : selectedTargets;
  const filterMap = {
    adCampaign: campaignSearchTermFilters,
    adTarget: targetSearchTermFilters
  };

  const searchBuilder = new FluentSearchFilterBuilder(filterMap);

  useEffect(() => {
    if (selectedData.size > 0 && editRuleId && initialSize === null) {
      setInitialSize(selectedData.size);
      setOriginalSelectedData(selectedData);
    }
  }, [selectedData, editRuleId, initialSize]);

  useEffect(() => {
    searchBuilder.setSearchTerm(debouncedSearchValue);
  }, [debouncedSearchValue, searchBuilder]);

  const getSearchFilters = useCallback(() => {
    const selector = () => {
      return {
        select: (filters: any) => filters[ruleTarget]
      };
    };
    return searchBuilder.setSearchTerm(debouncedSearchValue).withSelector(selector()).build();
  }, [debouncedSearchValue, ruleTarget, searchBuilder]);

  useEffect(() => {
    if (selectedData.size === 1000) {
      showSnackbar({
        message: 'You have selected the maximum number of items (1000).',
        type: 'info'
      });
    }
  }, [ruleTarget, selectedData, showSnackbar]);

  return (
    <Flex flexDirection="column">
      <Flex justifyContent="space-between">
        <Flex flexDirection="column" marginBottom="32px">
          <Text
            variant="h4"
            sx={{
              marginBottom: '8px'
            }}
          >
            Apply to {selectedTargetText}
          </Text>
          <BeaconSubtitleContainer>
            <Text variant="body1">
              The table displays the advertising metrics by{' '}
              {selectedTargetText.slice(0, selectedTargetText.length - 1).toLowerCase()} during the{' '}
              {formatDisplayName(mainTimePeriod)} period compared to the {comparisonDisplayName}.
            </Text>
          </BeaconSubtitleContainer>
          <SearchTextField
            placeholder={`Search ${ruleTarget === 'adCampaign' ? 'campaigns' : 'targets'}`}
            onChange={(e) => {
              e.preventDefault();
              setSearchTerm(e.target.value);
            }}
            value={searchTerm}
          />
        </Flex>

        {selectedData.size > 0 && (
          <Text
            sx={{
              border: `1px solid ${colors.graysSecondary}`,
              borderRadius: '16px',
              backgroundColor: colors.graysPrimary,
              color: colors.darkBlue,
              padding: '6px 20px',
              fontSize: '14px',
              fontWeight: 500,
              height: 'fit-content'
            }}
          >
            {selectedData.size} selected
          </Text>
        )}
      </Flex>
      <MetricListContainer
        skeletonRows={20}
        showHeader={false}
        headColumnDefinition={
          ruleTarget === 'adCampaign'
            ? [RulesCampaignCheckBox, CampaignColumnDefinitionNewTab, CampaignTypeField, CampaignAutomationField]
            : [RulesCampaignCheckBox, TargetColumnDefinition, TargetTypeColumnDefinition]
        }
        entityTableHeaderProps={{
          prefixTitle: 'Ad Metric by',
          title: ''
        }}
        indexName={GetAdMetricIndexName(entityType, ruleTarget)}
        fields={[
          AdClickField,
          AdSpendField,
          AdSalesField,
          ROASField,
          ...(ruleTarget === 'adCampaign' ? [] : [CPCField])
        ]}
        useTableDataProps={{
          aggregationKey: ruleTarget === 'adTarget' && 'targetingText',
          buildRequest: (builder: AdvancedSearchRequestBuilder) => {
            builder
              .removeConditionTermFilters((builderFilter) => builderFilter.fieldName === 'categoryId')
              .removeConditionRangeFilters((builderFilter) => builderFilter.fieldName === 'weekId')
              .addConditionRangeFilter('dayId', startDayId, endDayId);

            if (debouncedSearchValue !== '') {
              builder.addNestedFilterCondition('should', getSearchFilters());
            }
            return builder;
          },
          buildAggregation: (builder: AggregationBuilder) => {
            return builder
              .addConditionRangeFilter('dayId', startDayId, endDayId)
              .removeConditionTermFilter('weekId')
              .removeConditionRangeFilters('weekId')
              .clearComparisonRangeFilters()
              .addComparisonRangeFilter('dayId', startDayId, endDayId);
          },
          groupByFieldName: ruleTarget === 'adCampaign' ? 'campaignId' : 'targetingText,targetingType',
          filterResponse: () => true,
          itemsPerPage: 1000
        }}
        sortFieldName="clicks"
        showGrid={false}
        groupBy={ruleTarget === 'adCampaign' ? 'stacklineSku' : 'targetingText,targetingType'}
        postProcessData={(data) => {
          if (editRuleId && !debouncedSearchValue && selectedData.size === initialSize) {
            const sorted = sortData(data, ruleTarget, selectedData);
            return sorted;
          }

          return data.sort((a) => {
            const keyA = ruleTarget === 'adCampaign' ? a?.campaignId : `${a?.targetingText}_${a?.targetingType}`;
            return originalSelectedData?.has(keyA) ? -1 : 1;
          });
        }}
        exposeTableData={(data: any[]) => {
          if (!debouncedSearchValue) {
            if (ruleTarget === 'adCampaign' && !isEqual(campaigns, data)) {
              setCampaigns(data);
            }

            if (ruleTarget === 'adTarget' && !isEqual(targets, data) && data[0]?.targetingText) {
              setTargets(data);
            }
          }
        }}
      />
    </Flex>
  );
});

MetricGrid.displayName = 'MetricGrid';
